import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HiLightningBolt, HiStar, HiAcademicCap, HiBeaker, HiChip, HiCube, HiSparkles, HiCheckCircle } from 'react-icons/hi';
import { FaTrophy } from 'react-icons/fa';
import Confetti from 'react-confetti';

const levelIcons = [
  HiAcademicCap,  // Level 1 - Novice
  HiBeaker,       // Level 2 - Beginner
  HiLightningBolt,// Level 3 - Intermediate
  HiChip,         // Level 4 - Skilled
  HiCube,         // Level 5 - Advanced
  HiStar,         // Level 6 - Expert
  FaTrophy        // Level 7 - Guru
];

// Purple-themed color palette matching home page (7 levels)
// More varied purple palette for clearer differentiation between levels
const levelColors = [
  '#E9D5FF', // Level 1 - Novice - very light lavender
  '#D6BCFA', // Level 2 - Beginner - light orchid
  '#B794F4', // Level 3 - Intermediate - soft purple
  '#9F7AEA', // Level 4 - Skilled - medium violet
  '#7C3AED', // Level 5 - Advanced - vivid violet
  '#6D28D9', // Level 6 - Expert - deep violet
  '#4C1D95'  // Level 7 - Guru - indigo purple
];

const levelNames = [
  'AI Novice',
  'AI Explorer',
  'AI Practitioner',
  'AI Specialist',
  'AI Expert',
  'AI Master',
  'AI Guru'
];

// Fixed path positions - these define both the path AND node placement
// 7 levels total: Novice through Guru (Noob is NOT a level)
const PATH_POSITIONS = [
  { x: 50, y: 80 },   // Level 1 - Novice
  { x: 35, y: 180 },  // Level 2 - Beginner
  { x: 65, y: 280 },  // Level 3 - Intermediate
  { x: 40, y: 380 },  // Level 4 - Skilled
  { x: 60, y: 480 },  // Level 5 - Advanced
  { x: 45, y: 580 },  // Level 6 - Expert
  { x: 55, y: 680 }   // Level 7 - Guru
];

const ProgressPathScreen = ({ currentLevel = 1, completedLevels = [], previousLevelResult = null, showConfetti = false, onAnimationComplete }) => {
  const pathPositions = PATH_POSITIONS;
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [confettiActive, setConfettiActive] = useState(showConfetti);

  // Handle window resize for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stop confetti after a few seconds
  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setConfettiActive(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  // Keep the overall progress screen visible for the required duration
  useEffect(() => {
    const duration = 5500; // 5.5s
    const t = setTimeout(() => {
      if (onAnimationComplete) onAnimationComplete();
    }, duration + 500);
    return () => clearTimeout(t);
  }, [onAnimationComplete]);

  // Draw SVG path through all positions
  // Build smooth SVG path that passes through each node position
  // Using Catmull-Rom style curves for smooth path through points
  const pathData = (() => {
    if (pathPositions.length === 0) return '';
    
    let path = `M ${pathPositions[0].x} ${pathPositions[0].y}`;
    
    for (let i = 1; i < pathPositions.length; i++) {
      const curr = pathPositions[i];
      const prev = pathPositions[i - 1];
      
      // Calculate control points for smooth curve through the points
      const midX = (prev.x + curr.x) / 2;
      const midY = (prev.y + curr.y) / 2;
      
      // Use quadratic bezier with midpoint as control
      path += ` Q ${midX} ${midY}, ${curr.x} ${curr.y}`;
    }
    
    return path;
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 flex items-center justify-center p-4">
      {/* Confetti */}
      {confettiActive && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={300}
          gravity={0.3}
        />
      )}
      
      <div className="w-full max-w-2xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Your AI Journey</h1>
          <p className="text-lg text-gray-600 mb-4">
            {currentLevel === 1 ? 'Beginning your adventure...' : `Progressing to Level ${currentLevel}`}
          </p>
          
          {/* Previous Level Result - Show when coming from a completed level */}
          {previousLevelResult && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-4 inline-flex items-center gap-3 px-5 py-3 bg-green-50 border border-green-200 rounded-xl shadow-sm"
            >
              <HiCheckCircle className="text-green-500 text-2xl" />
              <div className="text-left">
                <div className="text-sm font-semibold text-green-700">
                  {previousLevelResult.levelName} Completed!
                </div>
                <div className="text-xs text-green-600">
                  {previousLevelResult.score} out of {previousLevelResult.total} correct
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Assessment Info - Single Line (only show for first level) */}
          {!previousLevelResult && (
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white rounded-full shadow-md text-sm text-gray-700 flex-wrap justify-center">
              <span>📝 <strong>5 questions</strong> per level</span>
              <span className="text-purple-400">•</span>
              <span>🎯 Get <strong>4 correct</strong> to advance</span>
              <span className="text-purple-400">•</span>
              <span>⏱️ <strong>4 minutes</strong> per level</span>
            </div>
          )}
        </motion.div>

        {/* Path Container */}
        <div className="relative" style={{ height: '750px' }}>
          {/* SVG Path */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 750"
            preserveAspectRatio="none"
          >
            {/* Background path - wider stroke for visual prominence */}
            <path
              d={pathData}
              fill="none"
              stroke="#E9D5FF"
              strokeWidth="6"
              strokeLinecap="round"
            />
            {/* Decorative gradient path on top */}
            <path
              d={pathData}
              fill="none"
              stroke="url(#gradient)"
              strokeWidth="4"
              strokeLinecap="round"
              opacity={0.85}
            />
            
            {/* Gradient definition - purple theme */}
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#A78BFA" />
                <stop offset="50%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#6D28D9" />
              </linearGradient>
            </defs>
          </svg>

          {/* Level Nodes */}
            {pathPositions.map((pos, index) => {
            const level = index + 1;
            const isCompleted = completedLevels.includes(level);
            const isCurrent = level === currentLevel;
            const isLocked = level > currentLevel && !isCompleted;
            const Icon = levelIcons[index];
            // Node positioning is static; only the current level performs a single activation animation
            const nodeStyle = {
              left: `${pos.x}%`,
              top: `${pos.y}px`,
              transform: 'translate(-50%, -50%)'
            };

            // Determine static appearance and animation props
            let initialProps = {};
            let animateProps = {};
            let transitionProps = { duration: 0.9, ease: 'easeOut' };

            if (isCurrent) {
              initialProps = { scale: 0, opacity: 0 };
              animateProps = { scale: [0, 1.12, 1], opacity: 1 };
            } else if (isCompleted) {
              initialProps = { scale: 1, opacity: 1 };
              animateProps = { scale: 1, opacity: 1 };
              transitionProps = { duration: 0 };
            } else {
              // locked
              initialProps = { scale: 0.9, opacity: 0.45 };
              animateProps = { scale: 0.9, opacity: 0.45 };
              transitionProps = { duration: 0 };
            }

            return (
              <motion.div
                key={level}
                className="absolute"
                style={nodeStyle}
                initial={initialProps}
                animate={animateProps}
                transition={transitionProps}
              >
                {/* Single-time soft glow for current level (no repeat) */}
                {isCurrent && (
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `radial-gradient(circle, ${levelColors[index]}40 0%, transparent 70%)`,
                      width: '120px',
                      height: '120px',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)'
                    }}
                    initial={{ scale: 0.8, opacity: 0.5 }}
                    animate={{ scale: 1.12, opacity: 0.8 }}
                    transition={{ duration: 0.9, ease: 'easeOut' }}
                  />
                )}

                {/* Node Circle with white outline to separate from path */}
                <div
                  className={`relative w-16 h-16 rounded-full flex items-center justify-center shadow-lg`}
                  style={{
                    backgroundColor: isLocked ? '#E9D5FF' : levelColors[index],
                    border: isCurrent ? '4px solid white' : '3px solid white',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}
                >
                  <Icon className={isLocked ? 'text-purple-300' : 'text-white'} style={{ fontSize: '1.5rem' }} />

                  {/* Level number badge */}
                  <div
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center text-xs font-bold"
                    style={{ 
                      color: isLocked ? '#A78BFA' : levelColors[index],
                      border: '2px solid white'
                    }}
                  >
                    {level}
                  </div>
                </div>

                {/* Level Name */}
                <div className="absolute top-full mt-2 whitespace-nowrap">
                  <div
                    className={`text-sm font-semibold text-center ${
                      isLocked ? 'text-purple-300' : 'text-purple-700'
                    }`}
                  >
                    {levelNames[index]}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgressPathScreen;
